// ============================================
// Register Engine — PJE v3.1 Bifurcated Persona System
//
// Manages three registers (analyst, guide, blended)
// with sentiment-driven kill switch and phase-aware
// register selection.
// ============================================

import type {
  RegisterType,
  NursePhase,
  ConversationState,
  Message,
} from '../../types/patientJourney';

import { getPJEConfig } from './pjeConfig';
import type { PJEConfig } from './pjeConfig';

// --- Helpers ---

/** Resolve config with fallback to default PJEConfig */
function resolveConfig(config?: PJEConfig): PJEConfig {
  return config ?? getPJEConfig();
}

/** Clamp a number to the range [min, max] */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// --- Sentiment ---

/**
 * Compute keyword-based sentiment scores for a list of messages.
 *
 * Only patient messages are scored; nurse/system messages receive a score of 0.
 * Each patient message is scanned for keywords in the config's sentimentKeywords
 * categories. Scores are clamped to [-100, 100].
 */
export function trackSentiment(
  messages: Message[],
  config?: PJEConfig,
): number[] {
  const cfg = resolveConfig(config);
  const { sentimentKeywords } = cfg;

  return messages.map((msg) => {
    // Only score patient messages
    if (msg.role !== 'patient') {
      return 0;
    }

    const text = msg.content.toLowerCase();
    let score = sentimentKeywords.defaultScore;

    // Strong negative keywords
    for (const word of sentimentKeywords.strongNegative.words) {
      if (text.includes(word.toLowerCase())) {
        score += sentimentKeywords.strongNegative.score;
      }
    }

    // Moderate negative keywords
    for (const word of sentimentKeywords.moderateNegative.words) {
      if (text.includes(word.toLowerCase())) {
        score += sentimentKeywords.moderateNegative.score;
      }
    }

    // Positive keywords
    for (const word of sentimentKeywords.positive.words) {
      if (text.includes(word.toLowerCase())) {
        score += sentimentKeywords.positive.score;
      }
    }

    return clamp(score, -100, 100);
  });
}

/**
 * Compute the sentiment baseline from the first N scores in a trajectory.
 *
 * Returns the average of the first `baselineMessageCount` scores. If fewer
 * scores are available, averages what's there. Returns 50 if no scores exist.
 */
export function computeSentimentBaseline(
  trajectory: number[],
  config?: PJEConfig,
): number {
  const cfg = resolveConfig(config);
  const baselineCount = cfg.sentimentKeywords.baselineMessageCount;

  if (trajectory.length === 0) {
    return 50;
  }

  const slice = trajectory.slice(0, baselineCount);
  const sum = slice.reduce((acc, val) => acc + val, 0);
  return sum / slice.length;
}

/**
 * Detect whether the most recent sentiment score deviates significantly
 * from the baseline.
 *
 * Returns true if the absolute difference between the last score and the
 * baseline exceeds `sentimentKillSwitchPct` percent of the baseline's
 * absolute magnitude (with a minimum magnitude of 1 to avoid division
 * edge cases when baseline is 0).
 *
 * Returns false if the trajectory is empty or has fewer entries than
 * the configured baselineMessageCount.
 */
export function detectSentimentDeviation(
  trajectory: number[],
  baseline: number,
  config?: PJEConfig,
): boolean {
  const cfg = resolveConfig(config);
  const baselineCount = cfg.sentimentKeywords.baselineMessageCount;

  if (trajectory.length === 0 || trajectory.length < baselineCount) {
    return false;
  }

  const latestScore = trajectory[trajectory.length - 1];
  const deviation = Math.abs(latestScore - baseline);
  // Use absolute magnitude of baseline with floor of 1 to avoid divide-by-zero
  const magnitude = Math.max(Math.abs(baseline), 1);
  const deviationPct = (deviation / magnitude) * 100;

  return deviationPct > cfg.sentimentKillSwitchPct;
}

// --- Register Selection ---

/**
 * Select the appropriate register for the current conversation turn.
 *
 * Phase 1 (scripted):
 *   - turnNumber <= 3 (data presentation)  -> config default for phase1_data
 *   - Goal/emotional conversation           -> config default for phase1_goals
 *   - Protocol presentation                 -> config default for phase1_protocol
 *
 * Phase 2 (freeform):
 *   - Default from config (typically 'blended')
 *   - TODO: Claude-based message content detection for dynamic switching
 *     (e.g. user asks for raw data -> 'analyst')
 *
 * Kill switch override: if sentiment deviation is detected, force 'guide'
 * regardless of phase or sub-phase.
 */
export function selectRegister(
  phase: NursePhase,
  conversationState: ConversationState,
  config?: PJEConfig,
): RegisterType {
  const cfg = resolveConfig(config);
  const { phaseRegisterDefaults } = cfg.registerConfig;

  // --- Sentiment kill switch: always check first ---
  const sentimentDeviation = detectSentimentDeviation(
    conversationState.sentimentTrajectory,
    conversationState.sentimentBaseline,
    cfg,
  );

  if (sentimentDeviation) {
    return 'guide';
  }

  // --- Phase 1: scripted ---
  if (phase === 'phase1_scripted') {
    // Data presentation sub-phase (first 3 turns)
    if (conversationState.turnNumber <= 3) {
      return phaseRegisterDefaults.phase1_data;
    }

    // Protocol has been presented — protocol review sub-phase
    if (conversationState.protocolPresented) {
      return phaseRegisterDefaults.phase1_protocol;
    }

    // Otherwise we're in the goals/emotional sub-phase
    return phaseRegisterDefaults.phase1_goals;
  }

  // --- Phase 2: freeform ---
  // Default register for phase 2 (typically 'blended')
  // TODO: Claude-based content detection — if user asks for specific data
  // or evidence, dynamically switch to 'analyst'. This requires message
  // content analysis which will be implemented with the Claude integration.
  return phaseRegisterDefaults.phase2_freeform;
}

// --- Prompt Fragment ---

/**
 * Return the system prompt fragment for a given register.
 *
 * The prompt fragment is injected into the system prompt to shape the
 * AI nurse's tone, structure, and behavioral constraints.
 */
export function getRegisterPromptFragment(
  register: RegisterType,
  config?: PJEConfig,
): string {
  const cfg = resolveConfig(config);
  return cfg.registerConfig.prompts[register];
}
